import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Handshake, ChevronLeft, Search, Loader2, ArrowUpDown, TrendingUp,
  CheckCircle, XCircle, Users, Info,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import {
  getEngageSourceBadge, ENGAGE_SOURCE_LABEL,
} from '../../../utils/engageStageBadge';


const ACTIVE_STATUSES = new Set([
  'new', 'contacted', 'interested', 'appointment_booked', 'consulted',
  'under_treatment', 'follow_up_required',
]);


export default function ReferralSourcesSettings() {
  const navigate = useNavigate();
  const { fetch: apiFetch } = useApiMethods();

  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('total');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch('engage/enquiries/?limit=500');
        const rows = resp?.results || resp?.data?.results || [];
        setEnquiries(Array.isArray(rows) ? rows : []);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load enquiries');
        setEnquiries([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bySource = useMemo(() => {
    if (!enquiries) return [];
    const map = {};
    for (const e of enquiries) {
      const src = e.source || 'other';
      if (!map[src]) map[src] = {
        source:    src,
        total:     0,
        active:    0,
        converted: 0,
        dropped:   0,
        latest:    null,
      };
      const s = map[src];
      s.total += 1;
      if (ACTIVE_STATUSES.has(e.status)) s.active += 1;
      if (e.status === 'converted') s.converted += 1;
      if (e.status === 'dropped' || e.status === 'lost') s.dropped += 1;
      const created = e.created_date ? new Date(e.created_date).getTime() : 0;
      if (!s.latest || created > s.latest) s.latest = created;
    }
    return Object.values(map).map(s => ({
      ...s,
      conv_rate: s.total ? Math.round(s.converted / s.total * 100) : 0,
      // 90-day activity = "active" — used to render a badge
      is_active: s.latest ? (Date.now() - s.latest) < 90 * 86400000 : false,
    }));
  }, [enquiries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let r = bySource;
    if (q) {
      r = r.filter(s =>
        (s.source || '').toLowerCase().includes(q) ||
        (ENGAGE_SOURCE_LABEL[s.source] || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...r].sort((a, b) => {
      const x = a[sortKey], y = b[sortKey];
      if (typeof x === 'number') return (x - y) * dir;
      return (x || '').toString().toLowerCase() < (y || '').toString().toLowerCase() ? -1 * dir : 1 * dir;
    });
  }, [bySource, search, sortKey, sortDir]);

  const totals = useMemo(() => ({
    sources:   bySource.length,
    total:     bySource.reduce((s, r) => s + r.total, 0),
    active:    bySource.reduce((s, r) => s + r.active, 0),
    converted: bySource.reduce((s, r) => s + r.converted, 0),
    active_sources: bySource.filter(r => r.is_active).length,
  }), [bySource]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'source' ? 'asc' : 'desc'); }
  };

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white shadow-sm">
          <Handshake className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Referral Sources</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            All patient inflow channels — click a row to see every enquiry from that source.
          </p>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Sources"          value={totals.sources}         icon={Handshake} tone="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400" />
        <StatCard label="Active (90d)"     value={totals.active_sources}  icon={TrendingUp} tone="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" />
        <StatCard label="Total enquiries"  value={totals.total}           icon={Users}     tone="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400" />
        <StatCard label="In pipeline"      value={totals.active}          icon={TrendingUp} tone="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400" />
        <StatCard label="Converted"        value={totals.converted}       icon={CheckCircle} tone="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" valueTone="text-green-600 dark:text-green-400" />
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by source name…"
            className="w-full pl-10 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <ThSort label="Source"       onClick={() => toggleSort('source')}    active={sortKey === 'source'}    dir={sortDir} />
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <ThSort label="Total"        onClick={() => toggleSort('total')}     active={sortKey === 'total'}     dir={sortDir} right />
              <ThSort label="Active"       onClick={() => toggleSort('active')}    active={sortKey === 'active'}    dir={sortDir} right />
              <ThSort label="Converted"    onClick={() => toggleSort('converted')} active={sortKey === 'converted'} dir={sortDir} right />
              <ThSort label="Dropped"      onClick={() => toggleSort('dropped')}   active={sortKey === 'dropped'}   dir={sortDir} right />
              <ThSort label="Conv %"       onClick={() => toggleSort('conv_rate')} active={sortKey === 'conv_rate'} dir={sortDir} right />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading || enquiries === null ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Loading…
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                {search ? `No sources match "${search}"` : 'No enquiries yet.'}
              </td></tr>
            ) : filtered.map(s => (
              <tr key={s.source} onClick={() => navigate(`/engage/enquiries?source=${s.source}`)}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
                <td className="px-4 py-3">
                  <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(s.source))}>
                    {ENGAGE_SOURCE_LABEL[s.source] || s.source}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {s.is_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                      <CheckCircle className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400">
                      <XCircle className="w-3 h-3" /> Dormant
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white tabular-nums">{s.total}</td>
                <td className="px-4 py-3 text-right text-amber-600 dark:text-amber-400 tabular-nums">{s.active}</td>
                <td className="px-4 py-3 text-right text-green-600 dark:text-green-400 font-medium tabular-nums">{s.converted}</td>
                <td className="px-4 py-3 text-right text-red-500 tabular-nums">{s.dropped}</td>
                <td className="px-4 py-3 text-right">
                  <span className={cn(
                    'inline-block px-2 py-0.5 rounded-full text-[10px] font-medium tabular-nums',
                    s.conv_rate >= 30 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                    s.conv_rate >= 15 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                                        'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300',
                  )}>
                    {s.conv_rate}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-800/40 text-[11px] text-orange-700 dark:text-orange-300 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <p>
          Source values (e.g. <code>doctor_referral</code>, <code>digital_marketing</code>) are baked into the enquiry schema
          — you can't add new source codes here. To customise the behaviour of a source (default assignee, priority routing),
          use the referral-partner pages: Referring Doctors, Ambulance Partners, Corporate Accounts, Health Camps, Digital Marketing.
        </p>
      </div>
    </div>
  );
}


function StatCard({ label, value, icon: Icon, tone, valueTone }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">{label}</p>
          <p className={cn('text-xl font-bold mt-1', valueTone || 'text-gray-900 dark:text-white')}>{value}</p>
        </div>
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', tone)}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}


function ThSort({ label, onClick, active, dir, right }) {
  return (
    <th className={cn('px-4 py-2 font-medium', right ? 'text-right' : 'text-left')}>
      <button onClick={onClick} className={cn(
        'inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors',
        active && 'text-gray-900 dark:text-white',
      )}>
        {label}
        <ArrowUpDown className={cn('w-3 h-3', active ? 'text-orange-500' : 'text-gray-300')} />
        {active && <span className="text-[8px]">{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}
